import React from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Sparkles, Check, Crown } from "lucide-react";
import { toast } from "sonner";
import { API } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

export function usePaywall() {
  const { user } = useAuth();
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [mode, setMode] = React.useState("features"); // "features" or "quota"
  const sub = user?.subscription || {};
  const hasFeatures = !!sub.has_features;   // trial or paid — gates PDF/forecast/history/ads
  const isPaid = !!sub.is_premium;          // paid only — gates quotas
  const gate = (featureName) => {
    if (hasFeatures) return true;
    setReason(featureName); setMode("features"); setOpen(true);
    return false;
  };
  const gateQuota = (limitLabel) => {
    if (isPaid) return true;
    setReason(limitLabel); setMode("quota"); setOpen(true);
    return false;
  };
  return {
    open, setOpen, reason, gate, gateQuota,
    hasFeatures, isPaid, isPremium: hasFeatures, // keep isPremium for backwards compat = feature access
    Modal: <PaywallDialog open={open} onClose={()=>setOpen(false)} reason={reason} mode={mode}/>,
  };
}

export function PaywallDialog({ open, onClose, reason, mode = "features" }) {
  const nav = useNavigate();
  const isQuota = mode === "quota";
  return (
    <Dialog open={open} onOpenChange={(v)=>!v && onClose()}>
      <DialogContent data-testid="paywall-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Crown size={18} className="text-[hsl(var(--primary))]"/> {isQuota ? "Free-tier limit reached" : "Unlock Premium"}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {isQuota
            ? `You've hit the free-tier cap${reason ? ` (${reason})` : ""}. Subscribe to keep adding.`
            : (reason ? `${reason} is a Premium feature.` : "This is a Premium feature. Subscribe to unlock.")}
        </p>
        <ul className="mt-2 text-sm space-y-1.5">
          <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> Unlimited bills & income streams</li>
          <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> Newspaper-style PDF ledger export</li>
          <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> 6-month cashflow forecast</li>
          <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> Historical months (any month, any year)</li>
          <li className="flex gap-2"><Check size={16} className="text-[hsl(var(--paid-border))]"/> Ad-free experience</li>
        </ul>
        <Button className="w-full mt-3" onClick={()=>{ onClose(); nav("/pricing"); }} data-testid="paywall-see-plans">See plans</Button>
      </DialogContent>
    </Dialog>
  );
}

export function TrialBanner() {
  const { user } = useAuth();
  const nav = useNavigate();
  const sub = user?.subscription;
  if (!sub) return null;
  if (sub.status === "active") return null;

  if (sub.status === "trialing") {
    const days = sub.days_left ?? 0;
    return (
      <div className="bg-[hsl(var(--payday-bg))] border-b border-[hsl(var(--payday-border))] text-[hsl(var(--payday-text))] text-xs sm:text-sm px-4 py-2 flex items-center justify-center gap-3 no-print" data-testid="trial-banner">
        <Sparkles size={14}/>
        <span><b>{days} {days === 1 ? "day" : "days"}</b> left of your free trial · Premium features unlocked. <span className="opacity-75">(Volume caps still apply until you subscribe.)</span></span>
        <button className="underline font-semibold" onClick={()=>nav("/pricing")} data-testid="trial-banner-upgrade">Upgrade now</button>
      </div>
    );
  }
  // expired / past_due / canceled
  return (
    <div className="bg-[hsl(var(--overdue-bg))] border-b border-[hsl(var(--overdue-border))] text-[hsl(var(--overdue-text))] text-xs sm:text-sm px-4 py-2 flex items-center justify-center gap-3 no-print" data-testid="trial-expired-banner">
      Trial ended — you're on the Free tier (5 bills, 2 incomes).
      <button className="underline font-semibold" onClick={()=>nav("/pricing")} data-testid="expired-banner-upgrade">Reclaim Premium</button>
    </div>
  );
}

export async function startCheckout(lookupKey) {
  try {
    const { data } = await axios.post(`${API}/payments/checkout`, {
      lookup_key: lookupKey,
      origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  } catch (e) {
    toast.error("Could not start checkout — try again.");
  }
}

export async function openPortal() {
  try {
    const { data } = await axios.post(`${API}/payments/portal`);
    window.location.href = data.url;
  } catch (e) {
    toast.error(e.response?.data?.detail || "No active subscription to manage.");
  }
}
